import React from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import { DollarIcon, AppleIcon, GoogleIcon } from "./icons";
import { PriceProps, PriceStore } from "./types";

const STORE_ICON: Record<PriceStore, React.FC<React.SVGProps<SVGSVGElement>>> = {
  appStore: AppleIcon,
  googleStore: GoogleIcon,
};

/**
 * Price card (Figma 568:75377 table / 476:68353 special). Pure presentational:
 * level tabs + either a price table (service columns × App Store / Google Play)
 * or a message block. White card surface flips in dark mode.
 */
export const Price: React.FC<PriceProps> = ({
  tabs,
  activeTab,
  currentLevel,
  onTabChange,
  columns,
  message,
  renderStoreIcon,
  className,
}) => {
  const storeIcon = (store: PriceStore) => {
    if (renderStoreIcon) return renderStoreIcon(store);
    const Icon = STORE_ICON[store];
    return <Icon className={styles.storeIcon} />;
  };

  return (
    <div className={cs(styles.card, className)}>
      <div className={styles.head}>
        <span className={styles.iconBox}>
          <DollarIcon className={styles.icon20} />
        </span>
        <h3 className={styles.heading}>Price</h3>
      </div>

      <div className={styles.body}>
        <div className={styles.tabs} role="tablist">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={t.id === activeTab}
              className={cs(styles.tab, { [styles.tabActive]: t.id === activeTab })}
              onClick={() => onTabChange?.(t.id)}
            >
              {currentLevel === t.id && <span className={styles.dot} aria-hidden />}
              {t.label}
              {t.id === activeTab && (
                <span className={styles.tabUnderline} aria-hidden />
              )}
            </button>
          ))}
        </div>

        {message ? (
          <div className={styles.message}>{message}</div>
        ) : columns && columns.length ? (
          <div className={styles.table}>
            {columns.map((col) => (
              <div key={col.service} className={styles.column}>
                <div className={styles.colHead}>{col.service}</div>
                <div className={styles.amount}>
                  <span className={styles.storeIconBox}>
                    {storeIcon("appStore")}
                  </span>
                  <span className={styles.price}>{col.appStore ?? "-"}</span>
                </div>
                <div className={styles.amount}>
                  <span className={styles.storeIconBox}>
                    {storeIcon("googleStore")}
                  </span>
                  <span className={styles.price}>{col.googleStore ?? "-"}</span>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Price;
